import axios from 'axios';
import { tokenStorage } from '../utils/tokenStorage';

const getBaseURL = () => {
  const envURL = process.env.REACT_APP_API_URL;
  if (!envURL) {
    throw new Error('REACT_APP_API_URL environment variable is required');
  }
  return envURL;
};

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getApiAccessToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth-only API surface. Tecnvirons-Website is the marketing site + auth
// gateway; dashboard/admin-panel domain endpoints stay in the Telecalling
// frontend, which is where the rest of the app lives after login.
export const authAPI = {
  signup: (userData) => api.post('/auth/signup', userData, { timeout: 45000 }),
  login: (credentials) => api.post('/auth/login', credentials),
  verifyEmail: (data) => api.post('/auth/verify-email', data, { timeout: 45000 }),
  resendVerification: (identifierOrPayload) => api.post('/auth/resend-verification', identifierOrPayload, { timeout: 45000 }),
  forgotPassword: (payload) => api.post('/auth/forgot-password', payload, { timeout: 45000 }),
  resetPassword: (data) => api.post('/auth/reset-password', data),

  adminSignup: (signupData) => api.post('/auth/admin-users/signup', signupData, { timeout: 60000 }),
  adminLogin: (credentials) => api.post('/auth/admin-users/login', credentials),
  adminForgotPassword: (payload) => api.post('/auth/admin-users/forgot-password', payload, { timeout: 45000 }),
  adminResetPassword: (data) => api.post('/auth/admin-users/reset-password', data),

  rbacForgotPassword: (payload) => api.post('/rbac/forgot-password', payload),
  rbacResetPassword: (data) => api.post('/rbac/reset-password', data),
  adminRbacForgotPassword: (data) => api.post('/admin-rbac/forgot-password', data),
  adminRbacResetPassword: (data) => api.post('/admin-rbac/reset-password', data),

  googleAuth: (data) => api.post('/auth/google', data),
  getProfile: () => api.get('/auth/me'),
};

// Authentication helper functions — cookie-backed for cross-subdomain support
export const authHelpers = {
  isAuthenticated: () => tokenStorage.isAuthenticated(),

  getCurrentUser: () => tokenStorage.getCurrentUser(),

  setCurrentUser: (user) => tokenStorage.setCurrentUser(user),

  clearAuth: () => tokenStorage.clearAuth(),

  setAuthData: (tokens, user) => {
    // Critical security: clear any impersonation tokens before setting new auth data
    tokenStorage.clearAuth();

    tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken);
    tokenStorage.setCurrentUser(user);

    const clientId = user?.client_id || user?.clientId;
    if (clientId) {
      localStorage.setItem('hms_client_id', clientId);
    }
  },
};

export default api;
